import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Calculator, RefreshCw, Plus, Trash2, Pencil, X as XIcon, Leaf } from 'lucide-react';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid,
  PieChart, Pie, Cell, Legend, BarChart, Bar,
} from 'recharts';
import { API } from '../../App';
import { useAlert } from '../../context/AlertContext';
import useLegendToggle from '../../utils/useLegendToggle';

/**
 * Emissions panel — calculate, summary, by-facility, factors CRUD.
 */

const SCOPE_COLORS = { scope_1: '#dc2626', scope_2: '#2563eb', scope_3: '#7c3aed' };

export default function EmissionsPanel({ siteId }) {
  const { showSuccess, showError, showInfo } = useAlert();
  const [summary, setSummary] = useState(null);
  const [byFacility, setByFacility] = useState([]);
  const [factors, setFactors] = useState([]);
  const [loading, setLoading] = useState(false);
  // Click-to-toggle legend for the by-scope donut.
  const scopeLegend = useLegendToggle();
  const [showCreateFactor, setShowCreateFactor] = useState(false);
  const [editingFactorId, setEditingFactorId] = useState(null);  // id of factor being edited

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (siteId) params.set('site_id', siteId);
      const [s, f, ef] = await Promise.all([
        axios.get(`${API}/mrv/emissions/summary${params.toString() ? '?' + params : ''}`),
        axios.get(`${API}/mrv/emissions/by-facility`),
        axios.get(`${API}/mrv/emissions/factors`),
      ]);
      setSummary(s.data);
      setByFacility(f.data.by_facility || []);
      setFactors(ef.data.factors || []);
    } catch (e) {
      showError(e.response?.data?.detail || 'Failed to load emissions data');
    } finally {
      setLoading(false);
    }
  }, [siteId, showError]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const calculate = async (force = false) => {
    try {
      showInfo('Calculating emissions… this may take a moment');
      const res = await axios.post(`${API}/mrv/emissions/calculate?force=${force}`);
      const d = res.data || {};
      showSuccess(`Calc complete — ${d.calculated} calculated, ${d.no_factor} no_factor, ${d.skipped} skipped`);
      fetchAll();
    } catch (e) {
      showError(e.response?.data?.detail || 'Calc failed');
    }
  };

  const deleteFactor = async (id) => {
    if (!window.confirm('Delete this emission factor?')) return;
    try {
      await axios.delete(`${API}/mrv/emissions/factors/${id}`);
      showSuccess('Factor deleted');
      fetchAll();
    } catch (e) {
      showError(e.response?.data?.detail || 'Delete failed');
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-gray-900">Emissions</h2>
        <div className="flex gap-2">
          <button onClick={() => calculate(false)}
                  className="px-3 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-md text-sm font-medium inline-flex items-center gap-1">
            <Calculator className="w-4 h-4" /> Calculate emissions
          </button>
          <button onClick={() => calculate(true)}
                  className="px-3 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md">
            Recalc all
          </button>
          <button onClick={fetchAll} disabled={loading} className="p-2 text-gray-500 hover:text-gray-700">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Total + by-scope */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        <div className="lg:col-span-1 p-4 rounded-lg bg-emerald-50 border border-emerald-200">
          <div className="text-xs font-semibold uppercase tracking-wide text-emerald-700 mb-1">Total emissions</div>
          <div className="text-3xl font-bold text-gray-900">
            {summary?.total_tco2e != null ? Number(summary.total_tco2e).toFixed(2) : '—'}
          </div>
          <div className="text-xs text-gray-500 mt-1">tCO2e (calculated only)</div>
        </div>
        <div className="lg:col-span-2 p-4 rounded-lg bg-white border border-gray-200">
          <div className="text-sm font-semibold text-gray-700 mb-2">By scope</div>
          {summary?.by_scope?.length ? (() => {
            // Keep all scopes in the data array so legend stays complete;
            // hidden ones get value=0 so the wedge collapses to 0° but the
            // legend entry stays clickable to bring it back.
            const renderData = summary.by_scope.map(s => ({
              ...s,
              tco2e: scopeLegend.isHidden(s.scope) ? 0 : s.tco2e,
            }));
            const visibleTotal = renderData.reduce((acc, r) => acc + (r.tco2e || 0), 0);
            return (
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie
                  data={renderData}
                  dataKey="tco2e"
                  nameKey="scope"
                  innerRadius={48}
                  outerRadius={78}
                  paddingAngle={2}
                  isAnimationActive={false}
                >
                  {renderData.map((s, i) => (
                    <Cell key={i} fill={SCOPE_COLORS[s.scope] || '#9ca3af'} />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(v, name) => [`${Number(v).toFixed(2)} tCO2e`, name]}
                  contentStyle={{ fontSize: 12 }}
                />
                <Legend
                  layout="vertical"
                  align="right"
                  verticalAlign="middle"
                  iconType="circle"
                  iconSize={9}
                  wrapperStyle={{ fontSize: 12 }}
                  onClick={(entry) => scopeLegend.onLegendClick({ value: entry.value })}
                  formatter={(value) => {
                    const row = summary.by_scope.find(r => r.scope === value);
                    if (!row) return value;
                    const pct = visibleTotal > 0 ? ((row.tco2e / visibleTotal) * 100).toFixed(0) : 0;
                    const dim = scopeLegend.isHidden(value);
                    return (
                      <span style={{
                        opacity: dim ? 0.4 : 1,
                        textDecoration: dim ? 'line-through' : 'none',
                        cursor: 'pointer',
                        userSelect: 'none',
                      }}
                      title={dim ? 'Click to show' : 'Click to hide'}
                      >{`${value} · ${Number(row.tco2e).toFixed(2)} tCO2e (${pct}%)`}</span>
                    );
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
            );
          })() : (
            <div className="text-sm text-gray-500 text-center py-6">No calculated emissions yet</div>
          )}
        </div>
      </div>

      {/* Trend */}
      {summary?.trend?.length > 0 && (
        <div className="mb-6 p-4 rounded-lg bg-white border border-gray-200">
          <div className="text-sm font-semibold text-gray-700 mb-2">Monthly trend</div>
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={summary.trend}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="month" tick={{ fontSize: 11 }}
                     tickFormatter={v => v ? new Date(v).toLocaleDateString(undefined, { month: 'short', year: '2-digit' }) : ''} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip formatter={v => `${Number(v).toFixed(2)} tCO2e`} />
              <Line type="monotone" dataKey="tco2e" stroke="#059669" strokeWidth={2} dot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* By facility */}
      {byFacility.length > 0 && (
        <div className="mb-6 p-4 rounded-lg bg-white border border-gray-200">
          <div className="text-sm font-semibold text-gray-700 mb-2">By facility / site</div>
          <ResponsiveContainer width="100%" height={Math.max(120, byFacility.length * 32)}>
            <BarChart data={byFacility} layout="vertical" margin={{ left: 16, right: 24 }}>
              <XAxis type="number" tick={{ fontSize: 11 }}
                     label={{ value: 'tCO2e', position: 'insideBottomRight', offset: -4, fontSize: 10, fill: '#6b7280' }} />
              <YAxis
                type="category"
                dataKey="site_name"
                width={180}
                tick={{ fontSize: 11 }}
                interval={0}
              />
              <Tooltip
                formatter={v => `${Number(v).toFixed(2)} tCO2e`}
                labelFormatter={v => v}
              />
              <Bar dataKey="tco2e" fill="#059669" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Top activity types */}
      {summary?.by_activity_type?.length > 0 && (
        <div className="mb-6 p-4 rounded-lg bg-white border border-gray-200">
          <div className="text-sm font-semibold text-gray-700 mb-2">Top activity types</div>
          <table className="w-full text-sm">
            <tbody className="divide-y divide-gray-100">
              {summary.by_activity_type.slice(0, 10).map((r, i) => (
                <tr key={i}>
                  <td className="py-1.5 text-gray-700">{r.activity_type}</td>
                  <td className="py-1.5 text-right font-mono text-emerald-700">
                    {Number(r.tco2e).toFixed(2)} tCO2e
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Emission factors */}
      <div className="mt-8">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-base font-semibold text-gray-900">Emission factors (per-tenant)</h3>
          <button onClick={() => setShowCreateFactor(s => !s)}
                  className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-md text-sm font-medium inline-flex items-center gap-1">
            <Plus className="w-4 h-4" /> Add factor
          </button>
        </div>

        {showCreateFactor && (
          <CreateFactorForm onCreated={() => { setShowCreateFactor(false); fetchAll(); }} onCancel={() => setShowCreateFactor(false)} />
        )}

        <div className="overflow-x-auto border border-gray-200 rounded-md">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50">
              <tr className="text-left">
                <th className="px-3 py-2 font-semibold text-gray-700">Activity Type</th>
                <th className="px-3 py-2 font-semibold text-gray-700">Region</th>
                <th className="px-3 py-2 font-semibold text-gray-700">Scope</th>
                <th className="px-3 py-2 font-semibold text-gray-700 text-right">Factor</th>
                <th className="px-3 py-2 font-semibold text-gray-700">Unit</th>
                <th className="px-3 py-2 font-semibold text-gray-700">Source</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {factors.length === 0 && (
                <tr><td colSpan={7} className="px-3 py-6 text-center text-gray-500">No emission factors defined</td></tr>
              )}
              {factors.map(f => (
                editingFactorId === f.id ? (
                  <EditFactorRow
                    key={f.id}
                    factor={f}
                    onSaved={() => { setEditingFactorId(null); fetchAll(); }}
                    onCancel={() => setEditingFactorId(null)}
                  />
                ) : (
                  <tr key={f.id} className="hover:bg-gray-50">
                    <td className="px-3 py-2 font-mono text-xs">{f.activity_type}</td>
                    <td className="px-3 py-2 text-gray-600">{f.region}</td>
                    <td className="px-3 py-2">
                      <span className="px-2 py-0.5 text-xs rounded bg-gray-100 text-gray-700">{f.scope}</span>
                    </td>
                    <td className="px-3 py-2 text-right font-mono">{f.factor_value}</td>
                    <td className="px-3 py-2 text-gray-600">{f.factor_unit}</td>
                    <td className="px-3 py-2 text-gray-600 text-xs">{f.source || '—'}</td>
                    <td className="px-3 py-2 text-right">
                      <button onClick={() => setEditingFactorId(f.id)}
                              className="text-blue-600 hover:text-blue-800 p-1" title="Edit">
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button onClick={() => deleteFactor(f.id)}
                              className="text-red-500 hover:text-red-700 p-1" title="Delete">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                )
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function CreateFactorForm({ onCreated, onCancel }) {
  const { showError, showSuccess } = useAlert();
  const [form, setForm] = useState({
    activity_type: 'electricity_consumption', region: 'India', scope: 'scope_2',
    factor_value: '', factor_unit: 'kgCO2e/kWh', source: '',
  });
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!form.factor_value) { showError('Factor value required'); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/mrv/emissions/factors`, {
        activity_type: form.activity_type,
        region: form.region,
        scope: form.scope,
        factor_value: parseFloat(form.factor_value),
        factor_unit: form.factor_unit,
        source: form.source || null,
      });
      showSuccess('Factor created');
      onCreated();
    } catch (e) {
      showError(e.response?.data?.detail || 'Create failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mb-3 p-4 border border-emerald-200 bg-emerald-50 rounded-md">
      <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-6 gap-2">
        <input placeholder="activity_type" value={form.activity_type}
               onChange={e => setForm(f => ({ ...f, activity_type: e.target.value }))}
               className="px-2 py-1 text-sm border rounded" />
        <input placeholder="region" value={form.region}
               onChange={e => setForm(f => ({ ...f, region: e.target.value }))}
               className="px-2 py-1 text-sm border rounded" />
        <select value={form.scope}
                onChange={e => setForm(f => ({ ...f, scope: e.target.value }))}
                className="px-2 py-1 text-sm border rounded">
          <option value="scope_1">scope_1</option>
          <option value="scope_2">scope_2</option>
          <option value="scope_3">scope_3</option>
        </select>
        <input type="number" step="any" placeholder="factor"
               value={form.factor_value}
               onChange={e => setForm(f => ({ ...f, factor_value: e.target.value }))}
               className="px-2 py-1 text-sm border rounded" />
        <input placeholder="factor_unit (e.g. kgCO2e/kWh)" value={form.factor_unit}
               onChange={e => setForm(f => ({ ...f, factor_unit: e.target.value }))}
               className="px-2 py-1 text-sm border rounded" />
        <input placeholder="source (e.g. CEA 2024)" value={form.source}
               onChange={e => setForm(f => ({ ...f, source: e.target.value }))}
               className="px-2 py-1 text-sm border rounded" />
      </div>
      <div className="mt-2 flex gap-2 justify-end">
        <button onClick={onCancel} className="px-3 py-1 text-sm text-gray-600 hover:text-gray-800">Cancel</button>
        <button onClick={submit} disabled={saving}
                className="px-3 py-1 bg-emerald-600 text-white rounded text-sm font-medium disabled:bg-gray-300">
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>
    </div>
  );
}

function EditFactorRow({ factor, onSaved, onCancel }) {
  const { showError, showSuccess } = useAlert();
  const [form, setForm] = useState({
    activity_type: factor.activity_type || '',
    region: factor.region || '',
    scope: factor.scope || 'scope_2',
    factor_value: factor.factor_value != null ? String(factor.factor_value) : '',
    factor_unit: factor.factor_unit || '',
    source: factor.source || '',
  });
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!form.factor_value) { showError('Factor value required'); return; }
    setSaving(true);
    try {
      await axios.patch(`${API}/mrv/emissions/factors/${factor.id}`, {
        activity_type: form.activity_type,
        region: form.region,
        scope: form.scope,
        factor_value: parseFloat(form.factor_value),
        factor_unit: form.factor_unit,
        source: form.source || null,
      });
      showSuccess('Factor updated');
      onSaved();
    } catch (e) {
      showError(e.response?.data?.detail || 'Update failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <tr className="bg-blue-50">
      <td className="px-3 py-2">
        <input value={form.activity_type}
               onChange={e => setForm(f => ({ ...f, activity_type: e.target.value }))}
               className="w-full px-2 py-1 text-xs border rounded font-mono" />
      </td>
      <td className="px-3 py-2">
        <input value={form.region}
               onChange={e => setForm(f => ({ ...f, region: e.target.value }))}
               className="w-full px-2 py-1 text-xs border rounded" />
      </td>
      <td className="px-3 py-2">
        <select value={form.scope}
                onChange={e => setForm(f => ({ ...f, scope: e.target.value }))}
                className="w-full px-2 py-1 text-xs border rounded">
          <option value="scope_1">scope_1</option>
          <option value="scope_2">scope_2</option>
          <option value="scope_3">scope_3</option>
        </select>
      </td>
      <td className="px-3 py-2">
        <input type="number" step="any"
               value={form.factor_value}
               onChange={e => setForm(f => ({ ...f, factor_value: e.target.value }))}
               className="w-full px-2 py-1 text-xs border rounded text-right font-mono" />
      </td>
      <td className="px-3 py-2">
        <input value={form.factor_unit}
               onChange={e => setForm(f => ({ ...f, factor_unit: e.target.value }))}
               className="w-full px-2 py-1 text-xs border rounded" />
      </td>
      <td className="px-3 py-2">
        <input value={form.source}
               onChange={e => setForm(f => ({ ...f, source: e.target.value }))}
               className="w-full px-2 py-1 text-xs border rounded" />
      </td>
      <td className="px-3 py-2 text-right whitespace-nowrap">
        <button onClick={submit} disabled={saving}
                className="px-2 py-1 bg-emerald-600 hover:bg-emerald-700 text-white rounded text-xs font-medium disabled:bg-gray-300">
          {saving ? 'Saving…' : 'Save'}
        </button>
        <button onClick={onCancel}
                className="ml-1 p-1 text-gray-500 hover:text-gray-700" title="Cancel">
          <XIcon className="w-4 h-4" />
        </button>
      </td>
    </tr>
  );
}
